import { useMemo } from 'react'

import { useModals, useSetState } from '@domain-group/react-hooks'

import { ModalContext } from '@domain-group/suit-theme'

import PageContext from '@contexts/pageContext'

import handleCallbackAction from '@functions/handleCallbackAction'
import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import AssignCampaignAgentsModal from '@modals/AssignCampaignAgentsModal'
import CreateCampaignDocumentModal from '@modals/CreateCampaignDocumentModal'
import CreateOrEditCampaignInspectionModal from '@modals/CreateOrEditCampaignInspectionModal'
import CreateOrEditMediaLinkModal from '@modals/CreateOrEditMediaLinkModal'

import {
  CampaignDocumentModel, CampaignInspectionModel, CampaignModel, MediaLinkModel,
} from '@models/types'

import PageContent from './components/PageContent'

export type PageContextPayload = {
  callbacks: {
    showAssignCampaignAgentsModal: () => void,
    showCreateCampaignDocumentModal: () => void,
    showCreateOrEditCampaignInspectionModal: () => void,
  },
}

const defaultState = {
  showAssignCampaignAgentsModal: false,
  showCreateCampaignDocumentModal: false,
  showCreateOrEditCampaignInspectionModal: false,
  showCreateOrEditMediaLinkModal: false,
}

type SetState = (state: Partial<typeof defaultState>) => void

/* eslint-disable no-undef */
const callbacks = (component: keyof typeof componentCallbacks, setState: SetState) => {
  const componentCallbacks = {
    AssignCampaignAgentsModal: {
      closeModal: () => setState({ showAssignCampaignAgentsModal: false }),
      updateCampaign: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    CreateCampaignDocumentModal: {
      closeModal: () => setState({ showCreateCampaignDocumentModal: false }),
      createCampaignDocument: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    CreateOrEditCampaignInspectionModal: {
      closeModal: () => setState({ showCreateOrEditCampaignInspectionModal: false }),
      createCampaignInspection: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      updateCampaignInspection: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    CreateOrEditMediaLinkModal: {
      closeModal: () => setState({ showCreateOrEditMediaLinkModal: false }),
      createMediaLink: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      updateMediaLink: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
  }

  return componentCallbacks[component] || {}
}

const PropertyDetails = () => {
  const [state, setState] = useSetState(defaultState)
  const {
    showAssignCampaignAgentsModal,
    showCreateCampaignDocumentModal,
    showCreateOrEditCampaignInspectionModal,
    showCreateOrEditMediaLinkModal,
  } = state

  const modalContext = useModals({ setState })
  const { callbacks: { setModalData } } = modalContext

  const pageContext = useMemo(() => ({
    callbacks: {
      geocodeAddress: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      showAssignCampaignAgentsModal: (payload: { campaign: CampaignModel }) => {
        setModalData('AssignCampaignAgentsModal', payload)
        setState({ showAssignCampaignAgentsModal: true })
      },
      showCreateCampaignDocumentModal: (payload: { campaignDocument: CampaignDocumentModel }) => {
        setModalData('CreateCampaignDocumentModal', payload)
        setState({ showCreateCampaignDocumentModal: true })
      },
      showCreateOrEditCampaignInspectionModal: (payload: { campaignInspection: CampaignInspectionModel }) => {
        setModalData('CreateOrEditCampaignInspectionModal', payload)
        setState({ showCreateOrEditCampaignInspectionModal: true })
      },
      showCreateOrEditMediaLinkModal: (payload: { mediaLink: MediaLinkModel }) => {
        setModalData('CreateOrEditMediaLinkModal', payload)
        setState({ showCreateOrEditMediaLinkModal: true })
      },
      updateCampaign: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
  }), [])

  return (
    <PageContext.Provider value={pageContext}>
      <ModalContext.Provider value={modalContext}>
        <PageContent />

        <AssignCampaignAgentsModal
          // @ts-expect-error TS2322
          callbacks={callbacks('AssignCampaignAgentsModal', setState)}
          showModal={showAssignCampaignAgentsModal}
        />

        <CreateCampaignDocumentModal
          // @ts-expect-error TS2322
          callbacks={callbacks('CreateCampaignDocumentModal', setState)}
          showModal={showCreateCampaignDocumentModal}
        />

        <CreateOrEditCampaignInspectionModal
          // @ts-expect-error TS2322
          callbacks={callbacks('CreateOrEditCampaignInspectionModal', setState)}
          showModal={showCreateOrEditCampaignInspectionModal}
        />

        <CreateOrEditMediaLinkModal
          // @ts-expect-error TS2322
          callbacks={callbacks('CreateOrEditMediaLinkModal', setState)}
          showModal={showCreateOrEditMediaLinkModal}
          permittedLinkTypes={['video', 'interactive', 'miniweb', 'other']}
        />
      </ModalContext.Provider>
    </PageContext.Provider>
  )
}

export default PropertyDetails
